import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function AnalyzePage() {
  const navigate = useNavigate();
  const [asin, setAsin] = useState('');
  const [error, setError] = useState('');
  const [isStarting, setIsStarting] = useState(false);

  const handleAnalyze = async () => {
    setError('');

    if (!asin.trim()) {
      setError('Please enter an ASIN');
      return;
    }

    setIsStarting(true);

    try {
      const { patentService } = await import('../../services/patentService');
      const result = await patentService.startReviewAnalysis(asin.trim());
      
      if (result.result_id) {
        navigate(`/review?review_id=${result.result_id}&asin=${encodeURIComponent(asin.trim())}`);
      } else {
        setError('Failed to start analysis. No result ID returned.');
        setIsStarting(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to start analysis. Please try again.');
      setIsStarting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAnalyze();
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center px-4 py-12">
      <div className="w-full max-w-2xl space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-white">
            Product Analysis
          </h1>
          <p className="text-lg text-gray-300">
            Enter an Amazon ASIN to analyze product patent risks
          </p>
        </div>

        {error && (
          <div className="bg-red-500/90 text-white border border-red-400 rounded-2xl px-4 py-3 text-sm shadow-lg">
            {error}
          </div>
        )}

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl p-8 space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-200">
              Amazon ASIN
            </label>
            <input
              type="text"
              value={asin}
              onChange={(e) => setAsin(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="e.g., B08N5WRWNW"
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              autoFocus
            />
            <p className="text-xs text-gray-400">
              Enter the Amazon Standard Identification Number to start analysis
            </p>
          </div>

          <button
            onClick={handleAnalyze}
            disabled={isStarting}
            className="w-full py-4 px-6 rounded-xl font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {isStarting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Starting Analysis...
              </span>
            ) : (
              'Start Analysis'
            )}
          </button>
        </div>

        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <h2 className="text-white font-semibold mb-3">What we analyze:</h2>
          <ul className="space-y-2 text-gray-300 text-sm">
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Product information and specifications</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Patent database search and matching</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Potential infringement risk assessment</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Detailed step-by-step analysis report</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

